import { useState } from "react";
import { ListFilter, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import {
  getAutomodWhitelist,
  addAutomodWhitelist,
  removeAutomodWhitelist,
  getModuleWhitelist,
  addModuleWhitelist,
  removeModuleWhitelist,
} from "@/api/automod";
import { showApiError } from "./helpers";
import { ChannelSelect, RoleSelect, ChoiceSelect } from "./fields";
import { MemberSelect, MemberTag } from "./MemberSelect";

const TARGET_TYPES = ["user", "role", "channel"];

const TargetPicker = ({ type, value, onChange, roles, channels, testId, guildId }) => {
  if (type === "role") return <RoleSelect value={value} onChange={onChange} roles={roles.filter((r) => !r.managed && r.name !== "@everyone")} testId={testId} />;
  if (type === "channel") return <ChannelSelect value={value} onChange={onChange} channels={channels} types={["text", "news", "voice", "stage_voice"]} testId={testId} />;
  return <MemberSelect guildId={guildId} value={value} onChange={onChange} placeholder="Search members…" testId={testId} includeBots={false} />;
};

const WhitelistTable = ({ title, items, onRemove, testId, roles, channels, showModule, guildId }) => {
  const label = (item) => {
    if (item.target_type === "role") return roles.find((r) => String(r.id) === String(item.target_id))?.name || item.target_id;
    if (item.target_type === "channel") {
      const ch = channels.find((c) => String(c.id) === String(item.target_id));
      return ch ? `#${ch.name}` : item.target_id;
    }
    return <MemberTag guildId={guildId} id={item.target_id} />;
  };
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">{title} ({items.length})</p>
      <div className="mt-3 space-y-2">
        {items.length === 0 && <p className="text-xs text-paper-500">Nothing whitelisted.</p>}
        {items.map((item, i) => (
          <div key={`${item.module || ""}-${item.target_type}-${item.target_id}-${i}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 px-3 py-2" data-testid={`${testId}-item-${i}`}>
            <span className="truncate text-sm text-paper-200">
              {showModule && <span className="mr-2 rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] text-paper-400">{item.module}</span>}
              <span className="mr-2 font-mono text-[10px] uppercase text-paper-500">{item.target_type}</span>
              {label(item)}
            </span>
            <button onClick={() => onRemove(item)} aria-label="Remove" data-testid={`${testId}-remove-${i}`} className="shrink-0 text-paper-500 transition-colors hover:text-paper-100">
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AutomodWhitelist = ({ guildId, roles, channels, modules }) => {
  const global_ = useApi(() => getAutomodWhitelist(guildId));
  const perModule = useApi(() => getModuleWhitelist(guildId));

  const [gType, setGType] = useState("user");
  const [gId, setGId] = useState("");
  const [mModule, setMModule] = useState("");
  const [mType, setMType] = useState("user");
  const [mId, setMId] = useState("");
  const [busy, setBusy] = useState(false);

  const moduleOptions = modules?.length ? modules : ["antilink", "antiinvite", "antispam", "antimention", "anticaps", "antiwordfilter"];

  const addGlobal = async () => {
    if (!gId) { toast.error("Pick a target first."); return; }
    setBusy(true);
    try {
      await addAutomodWhitelist(guildId, { target_id: gId, target_type: gType });
      toast.success("Added to automod whitelist.");
      setGId("");
      await global_.refetch();
    } catch (e) { showApiError(e); } finally { setBusy(false); }
  };

  const addForModule = async () => {
    if (!mModule || !mId) { toast.error("Pick a module and a target first."); return; }
    setBusy(true);
    try {
      await addModuleWhitelist(guildId, { module: mModule, target_id: mId, target_type: mType });
      toast.success("Added to module whitelist.");
      setMId("");
      await perModule.refetch();
    } catch (e) { showApiError(e); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="automod-whitelists">
      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">
          <ListFilter className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
          Add to global whitelist
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-[140px_1fr_auto]">
          <ChoiceSelect value={gType} onChange={(v) => { setGType(v); setGId(""); }} options={TARGET_TYPES} testId="automod-wl-type" />
          <TargetPicker type={gType} value={gId} onChange={setGId} roles={roles} channels={channels} guildId={guildId} testId="automod-wl-target" />
          <button onClick={addGlobal} disabled={busy} data-testid="automod-wl-add" className="rounded-full bg-white px-5 py-2 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50">
            Add
          </button>
        </div>
      </div>
      <WhitelistTable
        title="Global whitelist"
        items={global_.data?.whitelist || []}
        roles={roles}
        channels={channels}
        guildId={guildId}
        testId="automod-wl-table"
        onRemove={async (item) => {
          try {
            await removeAutomodWhitelist(guildId, { target_id: item.target_id, target_type: item.target_type });
            toast.success("Removed from whitelist.");
            await global_.refetch();
          } catch (e) { showApiError(e); }
        }}
      />
      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Add to module whitelist</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-[150px_1fr_auto]">
          <ChoiceSelect value={mModule} onChange={setMModule} options={moduleOptions} testId="automod-mwl-module" />
          <ChoiceSelect value={mType} onChange={(v) => { setMType(v); setMId(""); }} options={TARGET_TYPES} testId="automod-mwl-type" />
          <TargetPicker type={mType} value={mId} onChange={setMId} roles={roles} channels={channels} guildId={guildId} testId="automod-mwl-target" />
          <button onClick={addForModule} disabled={busy} data-testid="automod-mwl-add" className="rounded-full bg-white px-5 py-2.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50">
            Add
          </button>
        </div>
      </div>
      <WhitelistTable
        title="Module whitelist"
        items={perModule.data?.whitelist || []}
        roles={roles}
        channels={channels}
        guildId={guildId}
        showModule
        testId="automod-mwl-table"
        onRemove={async (item) => {
          try {
            await removeModuleWhitelist(guildId, { module: item.module, target_id: item.target_id, target_type: item.target_type });
            toast.success("Removed from module whitelist.");
            await perModule.refetch();
          } catch (e) { showApiError(e); }
        }}
      />
    </div>
  );
};
